// =====================================================
// SERVIÇO PARA GESTÃO DE TIPOS DE CONTA
// =====================================================

import { supabase } from '../lib/supabase';
import { 
  AccountTypeStats, 
  AccountConversionRequest, 
  PersonalAccountInfo, 
  BusinessAccountInfo,
  Tenant,
  TenantFeatures 
} from '../types/user';

export class AccountTypesService {

  // =====================================================
  // ESTATÍSTICAS E OVERVIEW
  // =====================================================

  /**
   * Obter estatísticas dos tipos de conta
   */
  async getAccountStats(): Promise<AccountTypeStats> {
    try {
      const { data, error } = await supabase
        .from('account_stats')
        .select('*');

      if (error) {
        console.error('❌ Erro ao buscar estatísticas:', error);
        
        // Fallback: calcular manualmente
        return await this.calculateStatsManually();
      }

      // Processar dados da view
      const stats: AccountTypeStats = {
        personal: {
          total: 0,
          active: 0,
          premium: 0
        },
        business: {
          total: 0,
          active: 0,
          premium: 0,
          avgUsersPerAccount: 0
        }
      };

      data?.forEach(row => {
        if (row.account_type === 'personal') {
          stats.personal = {
            total: row.total_accounts,
            active: row.active_accounts,
            premium: row.premium_accounts
          };
        } else if (row.account_type === 'business') {
          stats.business = {
            total: row.total_accounts,
            active: row.active_accounts,
            premium: row.premium_accounts,
            avgUsersPerAccount: Math.round(row.avg_users_per_account || 0)
          };
        }
      });

      return stats;
    } catch (error) {
      console.error('❌ Erro no serviço de estatísticas:', error);
      return await this.calculateStatsManually();
    }
  }

  /**
   * Calcular estatísticas manualmente (fallback)
   */
  private async calculateStatsManually(): Promise<AccountTypeStats> {
    try {
      // Buscar todos os tenants
      const { data: tenants, error } = await supabase
        .from('tenants')
        .select(`
          account_type,
          status,
          plan,
          id
        `);

      if (error) throw error;

      const stats: AccountTypeStats = {
        personal: { total: 0, active: 0, premium: 0 },
        business: { total: 0, active: 0, premium: 0, avgUsersPerAccount: 0 }
      };

      let businessUserCount = 0;
      let businessTenantCount = 0;

      for (const tenant of tenants || []) {
        const accountType = tenant.account_type || 'business'; // default

        if (accountType === 'personal') {
          stats.personal.total++;
          if (tenant.status === 'active') stats.personal.active++;
          if (tenant.plan === 'Premium') stats.personal.premium++;
        } else {
          stats.business.total++;
          businessTenantCount++;
          if (tenant.status === 'active') stats.business.active++;
          if (tenant.plan === 'Premium') stats.business.premium++;

          // Contar usuários deste tenant
          const { data: users } = await supabase
            .from('users')
            .select('id')
            .eq('tenant_id', tenant.id);
          
          businessUserCount += users?.length || 0;
        }
      }

      // Calcular média de usuários por conta business
      if (businessTenantCount > 0) {
        stats.business.avgUsersPerAccount = Math.round(businessUserCount / businessTenantCount);
      }

      return stats;
    } catch (error) {
      console.error('❌ Erro no cálculo manual:', error);
      // Retornar stats zeradas em caso de erro
      return {
        personal: { total: 0, active: 0, premium: 0 },
        business: { total: 0, active: 0, premium: 0, avgUsersPerAccount: 0 }
      };
    }
  }

  // =====================================================
  // LISTAGEM DE CONTAS
  // =====================================================

  /**
   * Listar contas pessoais
   */
  async getPersonalAccounts(): Promise<PersonalAccountInfo[]> {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select(`
          id,
          name,
          plan,
          created_at,
          features,
          personal_user_id,
          users!personal_user_id (
            id,
            name,
            email
          )
        `)
        .eq('account_type', 'personal')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map(tenant => ({
        tenantId: tenant.id,
        userId: tenant.personal_user_id || '',
        userName: tenant.users?.name || 'N/A',
        userEmail: tenant.users?.email || 'N/A',
        plan: tenant.plan,
        createdAt: new Date(tenant.created_at),
        features: tenant.features || this.getDefaultPersonalFeatures()
      }));
    } catch (error) {
      console.error('❌ Erro ao buscar contas pessoais:', error);
      return [];
    }
  }

  /**
   * Listar contas empresariais
   */
  async getBusinessAccounts(): Promise<BusinessAccountInfo[]> {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select(`
          id,
          name,
          plan,
          created_at,
          features,
          billing
        `)
        .eq('account_type', 'business')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const businessAccounts: BusinessAccountInfo[] = [];

      for (const tenant of data || []) {
        // Contar usuários
        const { data: users } = await supabase
          .from('users')
          .select('role')
          .eq('tenant_id', tenant.id);

        const userCount = users?.length || 0;
        const managerCount = users?.filter(u => u.role === 'manager').length || 0;

        businessAccounts.push({
          tenantId: tenant.id,
          companyName: tenant.name,
          userCount,
          managerCount,
          plan: tenant.plan,
          createdAt: new Date(tenant.created_at),
          features: tenant.features || this.getDefaultBusinessFeatures(),
          billing: tenant.billing || {}
        });
      }

      return businessAccounts;
    } catch (error) {
      console.error('❌ Erro ao buscar contas empresariais:', error);
      return [];
    }
  }

  // =====================================================
  // CONVERSÃO DE TIPOS DE CONTA
  // =====================================================

  /**
   * Converter conta para pessoal
   */
  async convertToPersonal(request: AccountConversionRequest): Promise<boolean> {
    try {
      if (!request.userId) {
        throw new Error('userId é obrigatório para conversão para conta pessoal');
      }

      const { data, error } = await supabase
        .rpc('convert_to_personal_account', {
          tenant_uuid: request.tenantId,
          user_uuid: request.userId
        });

      if (error) throw error;

      console.log('✅ Conta convertida para pessoal:', request.tenantId);
      return true;
    } catch (error) {
      console.error('❌ Erro ao converter para pessoal:', error);
      throw error;
    }
  }

  /**
   * Converter conta para empresarial
   */
  async convertToBusiness(request: AccountConversionRequest): Promise<boolean> {
    try {
      if (!request.companyName) {
        throw new Error('companyName é obrigatório para conversão para conta empresarial');
      }

      const { data, error } = await supabase
        .rpc('convert_to_business_account', {
          tenant_uuid: request.tenantId,
          company_name: request.companyName
        });

      if (error) throw error;

      console.log('✅ Conta convertida para empresarial:', request.tenantId);
      return true;
    } catch (error) {
      console.error('❌ Erro ao converter para empresarial:', error);
      throw error;
    }
  }

  // =====================================================
  // FEATURES PADRÃO
  // =====================================================

  /**
   * Features padrão para contas pessoais
   */
  private getDefaultPersonalFeatures(): TenantFeatures {
    return {
      multiUser: false,
      userInvitations: false,
      teamManagement: false,
      advancedSettings: false,
      customBranding: false,
      apiAccess: false,
      ssoIntegration: false,
      auditLogs: false
    };
  }

  /**
   * Features padrão para contas empresariais
   */
  private getDefaultBusinessFeatures(): TenantFeatures {
    return {
      multiUser: true,
      userInvitations: true,
      teamManagement: true,
      advancedSettings: true,
      customBranding: false, // opcional
      apiAccess: false, // opcional
      ssoIntegration: false, // opcional
      auditLogs: false // opcional
    };
  }

  // =====================================================
  // VERIFICAÇÕES
  // =====================================================

  /**
   * Verificar se uma conta pode ser convertida
   */
  async canConvertAccount(tenantId: string, targetType: 'personal' | 'business'): Promise<{ canConvert: boolean; reason?: string }> {
    try {
      // Buscar informações do tenant
      const { data: tenant, error } = await supabase
        .from('tenants')
        .select('account_type')
        .eq('id', tenantId)
        .single();

      if (error || !tenant) {
        return { canConvert: false, reason: 'Tenant não encontrado' };
      }

      if (tenant.account_type === targetType) {
        return { canConvert: false, reason: `Conta já é do tipo ${targetType}` };
      }

      // Para conversão para pessoal, verificar se há apenas 1 usuário
      if (targetType === 'personal') {
        const { data: users } = await supabase
          .from('users')
          .select('id')
          .eq('tenant_id', tenantId);

        if ((users?.length || 0) > 1) {
          return { 
            canConvert: false, 
            reason: 'Não é possível converter para conta pessoal: existem múltiplos usuários' 
          };
        }
      }

      return { canConvert: true };
    } catch (error) {
      console.error('❌ Erro ao verificar conversão:', error);
      return { canConvert: false, reason: 'Erro interno' };
    }
  }

  /**
   * Obter detalhes de uma conta específica
   */
  async getAccountDetails(tenantId: string): Promise<Tenant | null> {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select(`
          *,
          users (
            id,
            name,
            email,
            role,
            status,
            created_at
          )
        `)
        .eq('id', tenantId)
        .single();

      if (error) throw error;

      return {
        id: data.id,
        name: data.name,
        domain: data.domain,
        plan: data.plan,
        status: data.status,
        createdAt: new Date(data.created_at),
        settings: data.settings || {},
        billing: data.billing || {},
        contact: data.contact || {},
        accountType: data.account_type || 'business',
        isPersonal: data.is_personal || false,
        personalUserId: data.personal_user_id,
        maxUsers: data.max_users,
        features: data.features || (data.account_type === 'personal' ? 
          this.getDefaultPersonalFeatures() : 
          this.getDefaultBusinessFeatures())
      };
    } catch (error) {
      console.error('❌ Erro ao buscar detalhes da conta:', error);
      return null;
    }
  }
}

// =====================================================
// INSTÂNCIA SINGLETON
// =====================================================
export const accountTypesService = new AccountTypesService();
